import { FC, useEffect, useState, useRef } from "react";
import { toast } from "react-toastify";
import logo from "../../../assets/zampLogo.png";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { saveNotificationApi } from "./Notification.api";
import { FiClipboard, FiX } from "react-icons/fi";
import { useAuth } from "context/auth-context";

export interface ICard {
  survey?: any;
  getNotificationCount?: any;
  setNotificationCount?: any;
  notificationCount?: number;
}

export const SurveyNotification: FC<ICard> = ({
  survey,
  setNotificationCount,
  notificationCount,
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [message, setMessage] = useState("");
  const modalRef = useRef<HTMLDivElement>(null);
  const { auth } = useAuth();
  const queryClient = useQueryClient();
  const type = "survey";
  const mutation = useMutation(() => saveNotificationApi(auth?.panelistId, message, type), {
    onSuccess: (response: any) => {
      if (response.success) {
        queryClient.invalidateQueries(["saveNotificationApi"]);
      }
    },
    onError: () => {
      toast.error("Error while saving notification");
    },
  });

  const handleModalClose = () => {
    mutation.mutate();
    setNotificationCount(notificationCount + 1);
    setIsModalVisible(false);
  };

  // Alert logic with localStorage
  useEffect(() => {
    const today = new Date();
    const day = today.getDay();
    const todayDateKey = today.toDateString();
    const hasShownModalDate = localStorage.getItem("surveyModalShown");

    if ((day === 2 || day === 6) && hasShownModalDate !== todayDateKey) {
      const surveyName = survey?.studyName || "surveys";
      const surveyPoints = survey?.CPI || "more";

      const surveyMessage = `💡 Boost Your Rewards! Complete ${surveyName} for ${surveyPoints} points today and take your balance to the next level!`;
      const sendNotification = localStorage.getItem("sendNotification");
      if (sendNotification) {
        setMessage(surveyMessage);
        setIsModalVisible(true);
        localStorage.setItem("surveyModalShown", todayDateKey);
      }
    }
  }, [survey]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        handleModalClose();
      }
    };

    if (isModalVisible) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isModalVisible]);

  return (
    <div>
      {isModalVisible && (
        <div className="zs-backdrop" role="dialog" aria-modal="true" aria-labelledby="survey-notification-title">
          {/* Modal Content */}
          <div ref={modalRef} className="zs-modal max-w-[460px]">
            <button onClick={handleModalClose} className="zs-icon-btn zs-modal-close" aria-label="Close">
              <FiX />
            </button>
            <div className="flex items-start gap-4 p-5 pr-14 sm:p-6 sm:pr-16">
              <span className="w-12 h-12 p-1.5 shrink-0 zs-logo-plate rounded-lg">
                <img src={logo} alt="Survey Icon" className="object-contain w-full h-full" />
              </span>
              <div className="min-w-0">
                <p id="survey-notification-title" className="zs-eyebrow !mb-1">
                  <FiClipboard aria-hidden="true" />
                  Survey suggestion
                </p>
                <p className="text-[15px] leading-relaxed text-ink">{message}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
